import ArrowBack from '@material-ui/icons/ArrowBack';
import { IconButton } from '@monorepo/shared/componentsV2/Button/IconButton';
import { ButtonsGroup } from '@monorepo/shared/componentsV2/ButtonsGroup';
import { DocumentUploadFileInput } from '@monorepo/shared/componentsV2/documents/DocumentUploadFileInput';
import { Header } from '@monorepo/shared/componentsV2/Header';
import { Search } from '@monorepo/shared/componentsV2/Search';
import { VisuallyHidden } from '@monorepo/shared/componentsV2/VisuallyHidden';
import { useHasOrganizationUpdatePermissions } from '@monorepo/shared/hooks/permissions/useHasPermissions';
import { DocumentFolderWithFileCountResponse } from 'mapistry-shared';
import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useDocumentFoldersLink } from '../../hooks/useDocumentFolderLinks';
import { useUploadOrganizationDocuments } from '../../hooks/useUploadOrganizationDocuments';

const SearchContainer = styled.div`
  max-width: 10rem;
`;

interface FolderDocumentsHeaderProps {
  organizationId: string;
  folder: DocumentFolderWithFileCountResponse;
  filter: string;
  onSearch: (search: string) => void;
}

export function FolderDocumentsHeader({
  organizationId,
  folder,
  filter,
  onSearch,
}: FolderDocumentsHeaderProps) {
  const foldersLink = useDocumentFoldersLink();
  const navigate = useNavigate();
  const canEditOrgSettings =
    useHasOrganizationUpdatePermissions(organizationId);

  const [upload, { status }] = useUploadOrganizationDocuments({
    config: { throwOnError: true },
  });
  const isUploading = status === 'loading';

  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => onSearch(e.target.value),
    [onSearch],
  );

  const onUpload = useCallback(
    (files: File[]) => upload({ organizationId, folderId: folder.id, files }),
    [folder.id, organizationId, upload],
  );

  return (
    <Header
      title={folder.name}
      variant="noDropShadow"
      leftActions={
        <IconButton onClick={() => navigate(foldersLink)}>
          <ArrowBack />
          <VisuallyHidden>Back to all folders</VisuallyHidden>
        </IconButton>
      }
      rightActions={
        <ButtonsGroup>
          <SearchContainer>
            <Search onChange={handleSearch} value={filter} />
          </SearchContainer>
          {canEditOrgSettings && (
            <DocumentUploadFileInput
              inputId="folder-documents-upload"
              uploadButtonText="Add documents"
              isUploading={isUploading}
              onUpload={onUpload}
            />
          )}
        </ButtonsGroup>
      }
    />
  );
}
